/**
 * Keeps PMS screens in step with what the agent changes.
 *
 * When an approved agent action (or its undo) touches PMS data, the backend
 * says which data changed: domains ("availability", "bookings", "payments",
 * "inventory", "skills") plus room, booking and item ids. The agent UI
 * announces that here, and screens subscribe with usePmsDataRefresh to re-read
 * just that data in place, so nobody has to reload the page. Other open tabs of
 * the PMS hear the announcement too (BroadcastChannel).
 */
import { useEffect, useRef } from 'react';

export const PMS_DATA_CHANGED = 'dridh:pms-data-changed';
export const PMS_DATA_DOMAINS = ['bookings', 'availability', 'payments', 'inventory', 'skills'];

const CHANNEL_NAME = 'dridh-pms-data';
/** Ledger events older than this are history (for example on opening a conversation), not news. */
const LIVE_EVENT_MAX_AGE_MS = 2 * 60 * 1000;
const seenKeys = new Set();
let channel;

const idList = (value) => [...new Set((Array.isArray(value) ? value : []).filter((id) => typeof id === 'string' && id))];

export function normalizeChange(change) {
  if (!change || typeof change !== 'object') return null;
  const domains = idList(change.domains);
  if (domains.length === 0) return null;
  return {
    key: typeof change.key === 'string' ? change.key : null,
    source: change.source || 'agent',
    summary: typeof change.summary === 'string' ? change.summary : null,
    domains,
    roomIds: idList(change.roomIds),
    bookingIds: idList(change.bookingIds),
    itemIds: idList(change.itemIds),
  };
}

/** The change a live ACTION_EXECUTED / UNDO_EXECUTED ledger event describes, or null. */
export function changeFromLedgerEvent(event, now = Date.now()) {
  if (!event || (event.type !== 'ACTION_EXECUTED' && event.type !== 'UNDO_EXECUTED')) return null;
  const affected = event.payload?.affected;
  if (!affected) return null;
  const createdAt = Date.parse(event.createdAt);
  if (Number.isFinite(createdAt) && now - createdAt > LIVE_EVENT_MAX_AGE_MS) return null;
  return normalizeChange({
    ...affected,
    // An action executes once and is undone at most once, so type + action is the same key on every path.
    key: `${event.type}:${event.payload.actionId || event.id || event.seq}`,
    summary: event.payload.summary,
    source: event.type === 'UNDO_EXECUTED' ? 'agent-undo' : 'agent',
  });
}

/**
 * Announces every live executed or undone action among these ledger events,
 * each at most once. Conversations receive events both from the live stream and
 * from reloads (whichever arrives first), so both call this. Returns how many
 * changes were announced.
 */
export function announceLedgerEvents(events, now = Date.now()) {
  let announced = 0;
  for (const event of events || []) {
    const change = changeFromLedgerEvent(event, now);
    if (change && announcePmsDataChange(change)) announced += 1;
  }
  return announced;
}

/** Several changes as one: union of domains and ids, summaries in order. */
export function mergeChanges(changes) {
  const merged = { domains: [], roomIds: [], bookingIds: [], itemIds: [], summaries: [], count: 0 };
  for (const change of changes || []) {
    if (!change) continue;
    merged.count += 1;
    for (const field of ['domains', 'roomIds', 'bookingIds', 'itemIds']) merged[field] = idList([...merged[field], ...(change[field] || [])]);
    if (change.summary) merged.summaries.push(change.summary);
  }
  return merged;
}

function dispatchLocally(change) {
  if (change.key) {
    if (seenKeys.has(change.key)) return false;
    seenKeys.add(change.key);
    if (seenKeys.size > 500) seenKeys.delete(seenKeys.values().next().value);
  }
  window.dispatchEvent(new CustomEvent(PMS_DATA_CHANGED, { detail: change }));
  return true;
}

function getChannel() {
  if (channel !== undefined) return channel;
  channel = null;
  try {
    if (typeof BroadcastChannel === 'function') {
      channel = new BroadcastChannel(CHANNEL_NAME);
      // Node (tests) keeps the process alive for an open channel; browsers ignore this.
      if (typeof channel.unref === 'function') channel.unref();
      channel.onmessage = (message) => {
        const change = normalizeChange(message.data);
        if (change) dispatchLocally(change);
      };
    }
  } catch (error) {
    channel = null;
  }
  return channel;
}

/** Tells every subscribed screen (in this tab and other PMS tabs) that agent actions changed data. */
export function announcePmsDataChange(change) {
  const normalized = normalizeChange(change);
  if (!normalized) return false;
  const isNew = dispatchLocally(normalized);
  if (isNew) {
    try {
      getChannel()?.postMessage(normalized);
    } catch (error) {
      // another tab simply will not hear about it
    }
  }
  return isNew;
}

/**
 * Calls onChange once (debounced, merged) after agent actions change data in
 * any of the given domains. onChange receives the merged change, e.g.
 * usePmsDataRefresh(['availability'], ({ roomIds }) => reloadRooms(roomIds)).
 */
export function usePmsDataRefresh(domains, onChange, { delayMs = 350 } = {}) {
  const callback = useRef(onChange);
  callback.current = onChange;
  const domainKey = [...(domains || [])].sort().join(',');

  useEffect(() => {
    const wanted = new Set(domainKey.split(',').filter(Boolean));
    let pending = [];
    let timer = null;
    getChannel();

    const handle = (event) => {
      const change = event.detail;
      if (!change?.domains?.some((domain) => wanted.has(domain))) return;
      pending.push(change);
      clearTimeout(timer);
      timer = setTimeout(() => {
        const merged = mergeChanges(pending);
        pending = [];
        try {
          const result = callback.current?.(merged);
          if (result && typeof result.catch === 'function') {
            result.catch((error) => console.error('Refreshing after an agent change failed:', error));
          }
        } catch (error) {
          console.error('Refreshing after an agent change failed:', error);
        }
      }, delayMs);
    };

    window.addEventListener(PMS_DATA_CHANGED, handle);
    return () => {
      window.removeEventListener(PMS_DATA_CHANGED, handle);
      clearTimeout(timer);
    };
  }, [domainKey, delayMs]);
}
